import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { z } from "zod";

/** Zapis kliknięć/konwersji w tabeli cta_clicks — jedyna droga zapisu (klient nie ma już prawa INSERT). */

const slug = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).max(60);
const conversionId = z.enum(["konwersja:rezerwacja-wyslana", "konwersja:kontakt-wyslany"]);
const path = z.string().regex(/^\/[A-Za-z0-9\-._~/%]*$/).max(200);
const opt = (max: number) => z.string().trim().max(max).nullable().optional().transform((v) => v || null);

const schema = z
  .object({
    cta_id: z.union([slug, conversionId]),
    label: z.string().trim().min(1).max(120),
    location: z.string().trim().min(1).max(260),
    path,
    referrer: z.string().url().max(500).nullable().optional().transform((v) => v || null),
    utm_source: opt(200),
    utm_medium: opt(200),
    utm_campaign: opt(200),
    utm_term: opt(200),
    utm_content: opt(200),
    click_id: z.string().regex(/^(gclid|fbclid|msclkid|ttclid|li_fat_id):\S{1,200}$/).nullable().optional().transform((v) => v || null),
    landing_page: z.string().startsWith("/").max(500).nullable().optional().transform((v) => v || null),
  })
  .strict();

export type CtaClickInput = z.input<typeof schema>;

const PER_IP_PER_MINUTE = 60;
const GLOBAL_PER_MINUTE = 3000;

async function sha256(text: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 32);
}

export const logCtaClick = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => {
    // Nieprawidłowe dane odrzucamy po cichu, bez rzucania błędu.
    const parsed = schema.safeParse(input);
    return parsed.success ? parsed.data : null;
  })
  .handler(async ({ data }) => {
    if (!data) return { ok: false as const };
    // Konwersje tylko z formularzy, z pasującą ścieżką.
    if (data.cta_id === "konwersja:rezerwacja-wyslana" && data.path !== "/rezerwacja") return { ok: false as const };
    if (data.cta_id === "konwersja:kontakt-wyslany" && data.path !== "/kontakt") return { ok: false as const };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const ip =
      getRequestHeader("cf-connecting-ip") ||
      getRequestHeader("x-forwarded-for")?.split(",")[0]?.trim() ||
      "unknown";
    const ipBucket = `ip:${await sha256(ip)}`; // tylko skrót, adresu IP nie zapisujemy

    const [perIp, global] = await Promise.all([
      supabaseAdmin.rpc("cta_rate_hit", { _bucket: ipBucket, _limit: PER_IP_PER_MINUTE }),
      supabaseAdmin.rpc("cta_rate_hit", { _bucket: "global", _limit: GLOBAL_PER_MINUTE }),
    ]);
    if (perIp.data === false || global.data === false) return { ok: false as const, limited: true };

    const { error } = await supabaseAdmin.from("cta_clicks").insert(data);
    if (error) console.error("cta_clicks insert failed", error.message);
    return { ok: !error };
  });
